import React, { useId } from "react";
import type { LanguageLike } from "../utilities/languageCode";
import {
  isEnglishLanguageCode,
  isEstonianLanguageCode,
  isRussianLanguageCode,
  languageCodeFromActiveLanguage,
} from "../utilities/languageCode";

/** Minified SVG — keep in sync with {@link ESTONIA_FLAG_DATA_URI}. */
const ESTONIA_SVG_MARKUP =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 990 630">' +
  '<path fill="#0072ce" d="M0 0h990v315H0z"/>' +
  "<path d=\"M0 210h990v315H0z\"/>" +
  '<path fill="#fff" d="M0 420h990v210H0z"/>' +
  "</svg>";

/** Minified SVG — keep in sync with {@link RUSSIA_FLAG_DATA_URI}. */
const RUSSIA_SVG_MARKUP =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 6">' +
  '<path fill="#fff" d="M0 0h9v3H0z"/>' +
  '<path fill="#d52b1e" d="M0 3h9v3H0z"/>' +
  '<path fill="#0039a6" d="M0 2h9v2H0z"/>' +
  "</svg>";

/** For `<img src>` and react-select `url(...)` backgrounds — no network. */
export const ESTONIA_FLAG_DATA_URI =
  "data:image/svg+xml;charset=utf-8," + encodeURIComponent(ESTONIA_SVG_MARKUP);

export const RUSSIA_FLAG_DATA_URI =
  "data:image/svg+xml;charset=utf-8," + encodeURIComponent(RUSSIA_SVG_MARKUP);

/** Union Jack — keep in sync with {@link UK_FLAG_DATA_URI} and {@link UkFlagIcon}. */
const UK_SVG_MARKUP =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30">' +
  '<clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>' +
  '<clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>' +
  '<g clip-path="url(#s)">' +
  '<path d="M0,0 v30 h60 v-30 z" fill="#012169"/>' +
  '<path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/>' +
  '<path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#t)" stroke="#C8102E" stroke-width="4"/>' +
  '<path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/>' +
  '<path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/>' +
  "</g>" +
  "</svg>";

export const UK_FLAG_DATA_URI =
  "data:image/svg+xml;charset=utf-8," + encodeURIComponent(UK_SVG_MARKUP);

type IconProps = {
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
};

/** Inline Estonia flag (no download). */
export function EstoniaFlagIcon({
  width = 25,
  height = 15,
  className,
  style,
}: IconProps) {
  return (
    <svg
      viewBox="0 0 990 630"
      width={width}
      height={height}
      className={className}
      style={{ display: "block", flexShrink: 0, ...style }}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="m0 0h990v315H0z" fill="#0072ce" />
      <path d="m0 210h990v315H0z" />
      <path d="m0 420h990v210H0z" fill="#fff" />
    </svg>
  );
}

/** Inline UK flag (Union Jack) for English locales — no download. */
export function UkFlagIcon({
  width = 25,
  height = 15,
  className,
  style,
}: IconProps) {
  const rid = useId().replace(/:/g, "");
  const idS = `${rid}-uk-s`;
  const idT = `${rid}-uk-t`;
  return (
    <svg
      viewBox="0 0 60 30"
      width={width}
      height={height}
      className={className}
      style={{ display: "block", flexShrink: 0, ...style }}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <clipPath id={idS}>
          <path d="M0,0 v30 h60 v-30 z" />
        </clipPath>
        <clipPath id={idT}>
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${idS})`}>
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth={6} />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath={`url(#${idT})`}
          stroke="#C8102E"
          strokeWidth={4}
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth={10} />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth={6} />
      </g>
    </svg>
  );
}

/** Inline Russian flag (no download). */
export function RussiaFlagIcon({
  width = 25,
  height = 15,
  className,
  style,
}: IconProps) {
  return (
    <svg
      viewBox="0 0 9 6"
      width={width}
      height={height}
      className={className}
      style={{ display: "block", flexShrink: 0, ...style }}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path fill="#fff" d="M0 0h9v3H0z" />
      <path fill="#d52b1e" d="M0 3h9v3H0z" />
      <path fill="#0039a6" d="M0 2h9v2H0z" />
    </svg>
  );
}

/**
 * `background` for react-select flag `:before` — data URIs must be quoted or long SVG
 * flags (e.g. UK) break CSS `url()` parsing. Pass `"all"` for the Templates “All” option.
 */
export function cssBackgroundUrlFromLanguageSelectIcon(icon: string): string {
  if (icon === "all") return "all";
  const t = String(icon ?? "").trim();
  if (!t) return "none";
  return 'url("' + t.replace(/\\/g, "\\\\").replace(/"/g, '\\"') + '")';
}

/** Use embedded flags instead of a remote icon when `languageCode` matches. */
export function resolveLanguageFlagIconUrl(
  languageCode: string | null | undefined,
  remoteIconUrl: string
): string {
  if (isEstonianLanguageCode(languageCode)) {
    return ESTONIA_FLAG_DATA_URI;
  }
  if (isRussianLanguageCode(languageCode)) {
    return RUSSIA_FLAG_DATA_URI;
  }
  if (isEnglishLanguageCode(languageCode)) {
    return UK_FLAG_DATA_URI;
  }
  return remoteIconUrl;
}

/** Flag URL for a builder active-language row (V2 `icon`, nested `icon_url`, else built-ins). */
export function activeLanguageRowFlagUrl(
  lang: LanguageLike | null | undefined
): string {
  const code = languageCodeFromActiveLanguage(lang);
  const remote =
    (typeof lang?.icon === "string" && lang.icon.trim()) ||
    (typeof lang?.language?.icon_url === "string" &&
      lang.language.icon_url.trim()) ||
    "";
  return resolveLanguageFlagIconUrl(code, remote);
}
